/**
 * Login — Gmail + Apple sign-in (LOCKED model). No PIN/OTP.
 * Login is not a commit or ceremony, so it carries NO coloured primary:
 * both providers are neutral secondary (Gandharva semantic rule — Admin
 * QA File 1: "Login = neutral only, no primary").
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Phase 0 (2026-05-21, Anil) — `?return=` subdomain handoff, re-implemented
 *  from the login/launcher hotfix (tag `login-launcher-hotfix-2026-05-20`).
 *
 *  hr.aayatanveda.in / capture.aayatanveda.in deep-link into the portal with
 *  `?return=<their URL>` when a user is not yet signed in. After sign-in the
 *  user MUST be sent back to that subdomain — the pre-hotfix bug stranded
 *  them on `/` (→ /leads) instead. Hard rule:
 *
 *    If `?return=` is present AND points to a recognised *.aayatanveda.in
 *    surface, hand control back to that surface — never `/`. Order:
 *      1. avbp (same-origin)  → navigate locally, honour `?next=`.
 *      2. hr / capture        → mint a 60s SSO handoff token, redirect.
 *      3. ANY failure (no surface in claims, CF error, network, claims not
 *         yet resolved) → redirect to `<return>?legacy=1` so the destination
 *         shows its own login. The user is never stranded.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  browserLocalPersistence,
  setPersistence,
  signInWithCustomToken,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../lib/auth';
import { auth, functions } from '../lib/firebase';
import {
  mintHandoffUrl,
  parseReturnTarget,
  withLegacy,
} from '../lib/handoff';
import { appleSignInAvailable } from '../lib/platform';
import {
  backspaceFocus,
  blankOtp,
  fillFromPaste,
  formatLockTime,
  interpretRequest,
  interpretVerifyFailure,
  isValidHandle,
  joinOtp,
  normaliseHandle,
  OTP_LEN,
  setOtpDigit,
  type CodePhase,
  type RequestOtpResp,
  type VerifyOtpResp,
} from '../lib/clientOtpFlow';

export default function Login() {
  const { user, role, loading, error, signInGoogle, signInApple } = useAuth();
  // Apple sign-in is offered ONLY on iOS / Safari (locked auth model +
  // Apple branding rules). Google is universal. Computed once per mount.
  const showApple = appleSignInAvailable();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  /** true once a `?return=` handoff has started — suppresses the card. */
  const [handoffBusy, setHandoffBusy] = useState(false);
  /** Guards the handoff effect against re-firing (React 18 strict double-run
   *  + auth-context churn while claims resolve). */
  const handoffStarted = useRef(false);
  /** true once the secondary "Use a code" fallback is open (clients w/o Gmail). */
  const [codeMode, setCodeMode] = useState(false);

  const ret = parseReturnTarget(params.get('return'));

  // After sign-in: if `?return=` points to a recognised surface, drive the
  // handoff. Cross-subdomain (hr/capture) needs the role claims resolved
  // first (role !== null) so `surfaces[]` is populated before we decide
  // mint-vs-legacy. Same-origin (avbp) can navigate immediately.
  useEffect(() => {
    if (!user || !ret || handoffStarted.current) return;

    // Same-origin return — stay in the portal, honour `?next=`.
    if (ret.surface === 'avbp') {
      handoffStarted.current = true;
      const next = params.get('next') || '/';
      navigate(next, { replace: true });
      return;
    }

    // Cross-subdomain — wait until the claim resolution settles so
    // `surfaces[]` is trustworthy (auth.tsx sets `role` non-null once the
    // resolveTeamClaimsOnSignIn round-trip completes or falls back).
    if (role === null) return;

    handoffStarted.current = true;
    setHandoffBusy(true);

    // Guaranteed never-stranded fallback: open the destination with
    // ?legacy=1 so it renders its own login instead of looping back here.
    const fallback = () => {
      window.location.replace(withLegacy(ret.url));
    };

    void mintHandoffUrl(ret.surface, ret.url)
      .then((url) => {
        window.location.replace(url);
      })
      .catch((err) => {
        console.warn(
          '[Login] mintHandoffUrl failed — falling back to legacy login:',
          err,
        );
        fallback();
      });
  }, [user, role, ret, params, navigate]);

  if (loading) {
    return <div className="center-state">Restoring session…</div>;
  }

  // A `?return=` handoff is mid-flight (or about to start) — don't flash the
  // sign-in card or the dashboard underneath.
  if (user && ret) {
    return <div className="center-state">Returning you to your app…</div>;
  }

  // Signed in with no return target — straight to the portal.
  if (user) {
    return <Navigate to="/" replace />;
  }

  // handoffBusy is only ever true alongside `user` (set inside the effect),
  // so this is defensive; kept for clarity.
  if (handoffBusy) {
    return <div className="center-state">Returning you to your app…</div>;
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="mark" aria-label="Aayatan Veda">
          <img src="/av-mark.svg" alt="Aayatan Veda" />
        </div>
        <h1 className="serif">Aayatan Veda Portal</h1>
        <p>
          Sign in with your work Google account, or Apple ID. Access is bound to
          your role — there is nothing to set up.
        </p>

        {/* Prominent client note (owner-confirmed 2026-06-15): clients must
            sign in with the SAME email they gave Aayatan Veda, or their
            project will not appear under their account. */}
        <div className="auth-note" role="note">
          Sign in with the <strong>same email address you gave Aayatan Veda</strong> —
          otherwise your project won’t appear.
        </div>

        <div className="auth-btns">
          <button className="btn3 gh" onClick={signInGoogle}>
            <span aria-hidden>G</span> Continue with Google
          </button>
          {showApple && (
            <button className="btn3 gh" onClick={signInApple}>
              <span aria-hidden></span> Continue with Apple
            </button>
          )}
        </div>

        {error && (
          <div className="auth-err" role="alert">
            {error}
          </div>
        )}

        {/* Secondary, clearly-subordinate fallback for clients with no Gmail.
            Google/Apple above stay the primary path; this surfaces the
            already-live requestClientOtp/verifyClientOtp code flow so a client
            can sign themselves in without a Google account. */}
        {!codeMode ? (
          <button
            type="button"
            className="auth-codelink"
            onClick={() => setCodeMode(true)}
          >
            No Google account? Use a code instead
          </button>
        ) : (
          <CodeFallback onClose={() => setCodeMode(false)} />
        )}

        <div className="auth-foot">
          One portal. Your role decides what you see. portal.aayatanveda.in
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ Code fallback (clients without a Gmail) ════════════════════
 *
 * 2026-06-15 (owner-confirmed) — the self-serve entry point to the live
 * 4-digit code flow. Google/Apple stay primary on the card above; this is the
 * clearly-secondary path. It wires the ALREADY-DEPLOYED callables — no new
 * backend:
 *   requestClientOtp({ handle })  → code to email + WhatsApp
 *   verifyClientOtp({ handle, otp }) → custom token → signInWithCustomToken
 * After sign-in, auth.tsx resolves the isClient claim and ClientHome renders;
 * we navigate to "/" (the AppShell routes a client to their home). A `?return=`
 * /`?next=` handoff, if present, is honoured by the page-level effect once the
 * Firebase user appears — the same path Google sign-in takes.
 *
 * The pure transitions live in lib/clientOtpFlow.ts (unit-tested); this owns
 * only the round trips + the custom-token sign-in.
 */
function CodeFallback({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<CodePhase>('enter_handle');
  const [handle, setHandle] = useState('');
  const [digits, setDigits] = useState<string[]>(blankOtp());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const cellRefs = useRef<Array<HTMLInputElement | null>>(
    Array(OTP_LEN).fill(null),
  );

  // 90-day persistent session (the locked client model). Set once on mount.
  useEffect(() => {
    void setPersistence(auth, browserLocalPersistence).catch(() => {
      /* non-fatal — falls back to default persistence */
    });
  }, []);

  function focusCell(i: number) {
    if (i >= 0) setTimeout(() => cellRefs.current[i]?.focus(), 0);
  }

  async function requestCode(isResend = false) {
    if (!isValidHandle(handle)) {
      setError('Enter your email, phone, client number, or project number.');
      return;
    }
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const fn = httpsCallable<{ handle: string }, RequestOtpResp>(
        functions,
        'requestClientOtp',
      );
      const res = await fn({ handle: normaliseHandle(handle) });
      const out = interpretRequest(res.data);
      setPhase(out.phase);
      setMaskedEmail(out.maskedEmail);
      setMaskedPhone(out.maskedPhone);
      setLockUntil(out.lockUntil);
      if (out.error) setError(out.error);
      if (out.phase === 'enter_otp') {
        setDigits(blankOtp());
        if (isResend) setInfo('A new code has been sent.');
        focusCell(0);
      }
    } catch (e) {
      setError((e as { message?: string })?.message || 'Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(code: string) {
    setBusy(true);
    setError('');
    try {
      const fn = httpsCallable<{ handle: string; otp: string }, VerifyOtpResp>(
        functions,
        'verifyClientOtp',
      );
      const res = await fn({ handle: normaliseHandle(handle), otp: code });
      const data = res.data;
      if (data.ok) {
        // Sign in with the minted custom token; auth.tsx then resolves the
        // isClient claim on the next onAuthStateChanged + token refresh.
        await signInWithCustomToken(auth, data.customToken);
        try {
          await auth.currentUser?.getIdToken(true);
        } catch {
          /* non-fatal — auth.tsx refreshes again on its own */
        }
        // ClientHome lives at "/"; the page-level handoff effect honours any
        // ?return=/?next= once the user is present.
        navigate('/', { replace: true });
        return;
      }
      const fail = interpretVerifyFailure(data);
      setPhase(fail.phase);
      setLockUntil(fail.lockUntil);
      if (fail.error) setError(fail.error);
      if (fail.resetDigits) {
        setDigits(blankOtp());
        focusCell(0);
      }
    } catch (e) {
      setError((e as { message?: string })?.message || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  }

  function onDigit(i: number, raw: string) {
    const r = setOtpDigit(digits, i, raw);
    setDigits(r.digits);
    if (r.focus >= 0) focusCell(r.focus);
    if (r.complete) void verifyCode(joinOtp(r.digits));
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!/\d/.test(text)) return;
    e.preventDefault();
    const r = fillFromPaste(text);
    setDigits(r.digits);
    if (r.complete) void verifyCode(joinOtp(r.digits));
    else focusCell(r.digits.findIndex((d) => !d));
  }

  return (
    <div className="auth-code">
      {phase === 'enter_handle' && (
        <>
          <p className="auth-code-sub">
            We’ll send a 4-digit code to the email and WhatsApp on your account.
          </p>
          <input
            className="auth-input"
            type="text"
            autoComplete="username"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void requestCode();
            }}
            disabled={busy}
            placeholder="Email · phone · client # · project #"
            aria-label="Email, phone, client number, or project number"
          />
          <button
            type="button"
            className="btn3 ink"
            onClick={() => void requestCode()}
            disabled={busy}
            style={{ marginTop: 12, width: '100%' }}
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </>
      )}

      {phase === 'enter_otp' && (
        <>
          <p className="auth-code-sub">
            Sent to{' '}
            {maskedEmail ? <strong>{maskedEmail}</strong> : null}
            {maskedEmail && maskedPhone ? ' and ' : null}
            {maskedPhone ? <strong>{maskedPhone}</strong> : null}.
          </p>
          <div className="auth-otp-row">
            {Array.from({ length: OTP_LEN }).map((_, i) => (
              <input
                key={i}
                ref={(el) => {
                  cellRefs.current[i] = el;
                }}
                className="auth-otp-cell"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digits[i]}
                onChange={(e) => onDigit(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    const f = backspaceFocus(digits, i);
                    if (f >= 0) focusCell(f);
                  }
                }}
                onPaste={onPaste}
                disabled={busy}
                aria-label={`Code digit ${i + 1}`}
              />
            ))}
          </div>
          <div className="auth-code-actions">
            <button
              type="button"
              className="auth-codelink"
              onClick={() => {
                setPhase('enter_handle');
                setError('');
              }}
              disabled={busy}
            >
              Back
            </button>
            <button
              type="button"
              className="auth-codelink"
              onClick={() => void requestCode(true)}
              disabled={busy}
            >
              Resend code
            </button>
          </div>
        </>
      )}

      {phase === 'locked' && (
        <p className="auth-code-sub">
          Too many attempts. Sign-in is locked until{' '}
          <strong>{formatLockTime(lockUntil)}</strong>. Try Google sign-in above,
          or contact us.
        </p>
      )}

      {phase === 'unknown' && (
        <p className="auth-code-sub">
          We can’t find an account with that email, phone, or number. Try a
          different one, or contact us to set up access.
        </p>
      )}

      {phase === 'delivery_failed' && (
        <p className="auth-code-sub">
          We had trouble sending your code. Please try again in a moment.
        </p>
      )}

      {error && (
        <div className="auth-err" role="alert" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}
      {info && (
        <div className="auth-code-info" role="status">
          {info}
        </div>
      )}

      <button type="button" className="auth-codelink" onClick={onClose}>
        ← Back to Google sign-in
      </button>
    </div>
  );
}
